import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cx } from '../../lib/util/cx';

/** Port of the DS `IconButton` — 40×40, press is opacity .8, never a colour change. */
export type IconButtonTone = 'default' | 'warm' | 'onDark';

const TONE: Record<IconButtonTone, string> = {
  default: 'text-ink bg-transparent hover:bg-[var(--hp-gray-100)]',
  warm: 'text-brown-deep bg-[rgba(143,68,32,.06)] hover:bg-[rgba(143,68,32,.12)]',
  onDark: 'text-cream bg-transparent hover:bg-[rgba(241,237,225,.1)]',
};

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  /** Required: an icon-only control has no other accessible name. */
  ariaLabel: string;
  tone?: IconButtonTone;
  shape?: 'circle' | 'rounded';
  size?: number;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { ariaLabel, tone = 'default', shape = 'circle', size = 40, className, style, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={ariaLabel}
      title={ariaLabel}
      className={cx(
        'inline-flex items-center justify-center border-0 p-0 transition-[background-color,opacity] duration-fast',
        'active:opacity-80 disabled:cursor-not-allowed disabled:opacity-50',
        shape === 'circle' ? 'rounded-full' : 'rounded-[10px]',
        TONE[tone],
        className,
      )}
      style={{ width: size, height: size, ...style }}
      {...rest}
    >
      {children}
    </button>
  );
});
